import styled from '@emotion/styled'
import { ApiShopperLandingPageLink } from '@storefronts/common-types'
import { useContext } from 'react'
import { ViddyLPContext } from '../../context/viddyLPContext'
import { imageUrlTransform } from '../../utils/imageUrlTransform'
import { priceFormat } from '../../utils/priceFormat'
import {
    getDisplayData,
    resolveProductCardImageUrl,
} from '../../utils/productDisplay'
import { AddToCartInlineCTA } from '../landing-page/full-screen/AddToCart'
import { useProductGridVariantSelection } from '../shared/variant-options/useProductGridVariantSelection'

/**
 * The product the chat is about, kept one tap from the cart while its big
 * card is scrolled out of view. Adds like the storefront's grid card: a
 * stepper once in the cart, the variant picker for products with options.
 */
export const BrandChatProductBar: React.FC<{
    link: ApiShopperLandingPageLink
}> = ({ link }) => {
    const { landingPage } = useContext(ViddyLPContext)
    const productData = useProductGridVariantSelection({
        lpLink: link,
        showSwatches: false,
        preferAvailable: true,
    })
    const { activeVariant, price } = productData
    const displayData = getDisplayData(
        link,
        activeVariant?.shopifyProductId ?? null,
    )
    const image = imageUrlTransform(
        resolveProductCardImageUrl(activeVariant, link, displayData),
    )

    return (
        <Bar>
            {image && <Thumb src={image} alt="" />}
            <Text>
                <Name>{displayData.title ?? link.title}</Name>
                {price !== undefined && (
                    <Price>
                        {priceFormat(price, landingPage.client.currencySymbol)}
                    </Price>
                )}
            </Text>
            <Action>
                <AddToCartInlineCTA link={link} rowProductData={productData} />
            </Action>
        </Bar>
    )
}

const Bar = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px;
    border: 1px solid #ececec;
    border-radius: 16px;
    background: #ffffff;
    animation: barIn 220ms ease-out both;

    @keyframes barIn {
        from {
            opacity: 0;
            transform: translateY(6px);
        }
        to {
            opacity: 1;
            transform: none;
        }
    }

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`

const Thumb = styled.img`
    flex: none;
    width: 40px;
    height: 40px;
    border-radius: 10px;
    object-fit: cover;
    background: #f2f2f2;
`

const Text = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 1px;
`

const Name = styled.div`
    font-size: 14px;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`

const Price = styled.div`
    font-size: 13px;
    color: #6e6e73;
`

const Action = styled.div`
    flex: none;
    width: 128px;
`
